/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { type ReactNode, useState } from 'react';

import type { ApiOrgMarket, ApiOrgProperty, ApiPropertyProject, ApiUpdateOrgProperty } from '@kaiizen/kai-pro-api';
import { usePropertyHeroForOrgProperty } from '@kaiizen/kai-pro-api-react-state';
import { formatCurrency } from '@kaiizen/kai-reports';

import { usePropertyIdLabel } from '@/Client/Lib/usePropertyIdLabel';
import { useToast } from '@/Client/State/ToastContext';
import { DEFAULT_PROPERTY_OVERVIEW_HEAD_STYLES, type PropertyOverviewHeadStyles } from './propertyOverviewHeadStyles';
import {
  formatShortDate,
  getPropertyFactsDraft,
  getPropertyFactsUpdate,
  getPropertyHeadFigures,
  getPropertyJobsLifetimeTotal,
  type PropertyFactsDraft,
} from './propertyOverviewLib';

export const PROPERTY_FACTS_EDIT_LABEL = 'Edit';
export const PROPERTY_FACTS_SAVE_LABEL = 'Save info';
export const PROPERTY_FACTS_CANCEL_LABEL = 'Cancel';

type FactField = {
  key: keyof PropertyFactsDraft;
  label: string;
  unit?: string;
  inputType?: 'number' | 'text';
  /** A select's options; a plain input when absent. */
  options?: { value: string; label: string }[];
  /** The value shown while not editing; defaults to the draft string. */
  display?: string;
};

/** "2525 McDonald Way, Austin, TX 78754" → the street as the title, the rest under it. */
const splitAddress = (address: string | undefined) => {
  const [street = '', ...rest] = (address ?? '').split(',').map(part => part.trim());
  return { street, locality: rest.join(', ') };
};

/**
 * The top of the property overview: the property's photo, its address with a link to its market,
 * the figures that summarize its jobs, and its facts - which an editor changes in place, all at
 * once, with Edit / Cancel / Save info.
 */
export const PropertyOverviewHead = ({
  orgProperty,
  orgMarket,
  markets,
  projects,
  canEdit,
  saveProperty,
  onViewMarket,
  onShowJobs,
  styles = DEFAULT_PROPERTY_OVERVIEW_HEAD_STYLES,
}: {
  orgProperty: ApiOrgProperty;
  orgMarket?: ApiOrgMarket;
  markets: readonly ApiOrgMarket[];
  /** Every job at the property, archived ones included. */
  projects: readonly ApiPropertyProject[];
  canEdit: boolean;
  saveProperty: (update: ApiUpdateOrgProperty) => Promise<unknown>;
  onViewMarket?: () => void;
  /** Brings the jobs table into view. */
  onShowJobs: () => void;
  styles?: PropertyOverviewHeadStyles;
}) => {
  const heroUri = usePropertyHeroForOrgProperty(orgProperty.id);
  const propertyIdLabel = usePropertyIdLabel();
  const { showErrorToast } = useToast();
  const [draft, setDraft] = useState<PropertyFactsDraft>();
  const [saving, setSaving] = useState(false);
  const editing = !!draft;

  const { street, locality } = splitAddress(orgProperty.address);
  const { activeJobCount, lastUpdated } = getPropertyHeadFigures(projects);
  const shown = draft ?? getPropertyFactsDraft(orgProperty);

  const fields: FactField[] = [
    { key: 'bedroomCount', label: 'Beds', inputType: 'number' },
    { key: 'fullBathCount', label: 'Full baths', inputType: 'number' },
    { key: 'halfBathCount', label: 'Half baths', inputType: 'number' },
    { key: 'squareFeet', label: 'Square feet', inputType: 'number', unit: 'sqft' },
    { key: 'acres', label: 'Acres', inputType: 'number', unit: 'acres' },
    { key: 'hasGarage', label: 'Garage', options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] },
    {
      key: 'orgMarketId',
      label: 'Market',
      options: markets.map(market => ({ value: market.id, label: market.name })),
      display: orgMarket?.name,
    },
    { key: 'propertyId', label: propertyIdLabel, inputType: 'text' },
  ];

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await saveProperty(getPropertyFactsUpdate(draft, orgProperty));
      setDraft(undefined);
    } catch (error) {
      showErrorToast(error, 'save the property');
    } finally {
      setSaving(false);
    }
  };

  const renderValue = (field: FactField): ReactNode => {
    if (editing) {
      const id = `property-fact-${field.key}`;
      const onChange = (value: string) => setDraft(current => current && { ...current, [field.key]: value });
      return field.options
        ? (
          <select id={id} css={styles.factInput} aria-label={field.label} value={shown[field.key]} onChange={event => onChange(event.target.value)}>
            <option value=''>—</option>
            {field.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        )
        : (
          <input
            id={id}
            css={styles.factInput}
            aria-label={field.label}
            type={field.inputType ?? 'text'}
            value={shown[field.key]}
            onChange={event => onChange(event.target.value)}
          />
        );
    }
    const value = field.display ?? (field.options ? field.options.find(option => option.value === shown[field.key])?.label : shown[field.key]);
    return value
      ? (
        <>
          {value}
          {field.unit ? <span css={styles.factUnit}>{field.unit}</span> : null}
        </>
      )
      : <span css={styles.shared.dash}>—</span>;
  };

  return (
    <section css={styles.root} aria-label='Property'>
      <figure css={[styles.photo, heroUri ? css`background-image: url(${heroUri});` : undefined]} role='img' aria-label={`Photo of ${street}`} />
      <div css={styles.body}>
        <header css={styles.titleRow}>
          <div css={styles.titleText}>
            <h1 css={styles.title}>{street}</h1>
            <div css={styles.subtitle}>
              {locality}
              {orgMarket && onViewMarket
                ? (
                  <>
                    {locality ? '\u00a0\u00a0·\u00a0\u00a0' : null}
                    <button type='button' css={styles.link} onClick={onViewMarket}>View market</button>
                  </>
                )
                : null}
            </div>
          </div>
          <div css={[styles.stat, styles.leadStat]}>
            <span css={[styles.statValue, styles.leadStatValue]} data-testid='property-lifetime-total'>
              {formatCurrency(getPropertyJobsLifetimeTotal(projects))}
            </span>
            <span css={styles.statKey}>Lifetime</span>
          </div>
        </header>

        <div css={styles.stats}>
          <button type='button' css={[styles.stat, styles.statLink]} onClick={onShowJobs}>
            <span css={styles.statValue}>{activeJobCount}</span>
            <span css={styles.statKey}>Active jobs</span>
          </button>
          <div css={styles.stat}>
            <span css={styles.statValue}>{formatShortDate(lastUpdated) ?? '—'}</span>
            <span css={styles.statKey}>Last updated</span>
          </div>
        </div>

        <div css={styles.factsBand}>
          <dl css={styles.facts} aria-label='Property facts'>
            {fields.map(field => (
              <div key={field.key} css={styles.fact}>
                <dt css={styles.factKey}>
                  {editing ? <label htmlFor={`property-fact-${field.key}`}>{field.label}</label> : field.label}
                </dt>
                <dd css={styles.factValue}>{renderValue(field)}</dd>
              </div>
            ))}
          </dl>

          {canEdit
            ? (
              <div css={styles.factsActions}>
                {editing
                  ? (
                    <>
                      <button type='button' css={[styles.shared.button, styles.actionButton]} disabled={saving} onClick={() => setDraft(undefined)}>
                        {PROPERTY_FACTS_CANCEL_LABEL}
                      </button>
                      <button
                        type='button'
                        css={[styles.shared.button, styles.shared.primaryButton, styles.actionButton]}
                        disabled={saving}
                        onClick={() => void save()}
                      >
                        {saving ? 'Saving…' : PROPERTY_FACTS_SAVE_LABEL}
                      </button>
                    </>
                  )
                  : (
                    <button type='button' css={styles.editLink} onClick={() => setDraft(getPropertyFactsDraft(orgProperty))}>
                      {PROPERTY_FACTS_EDIT_LABEL}
                    </button>
                  )}
              </div>
            )
            : null}
        </div>
      </div>
    </section>
  );
};
